/**
 * Admin Messages/Support Management
 * Contact form messages with status tracking and internal notes
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApiGet, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  Calendar,
  ChatText,
  CheckCircle,
  CircleNotch,
  Clock,
  Envelope,
  FloppyDisk,
  Funnel,
  MagnifyingGlass,
  Tag,
  User,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('30d');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [internalNote, setInternalNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/messages', {
        type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        range: dateFilter,
      });
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await adminApiPatch(`/api/admin/messages/${messageId}`, { status: newStatus });
      toast.success('Status updated');
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleSaveNote = async () => {
    if (!selectedMessage || !internalNote.trim()) return;

    try {
      setSavingNote(true);
      await adminApiPatch(`/api/admin/messages/${selectedMessage.id}`, {
        internal_notes: internalNote,
      });
      toast.success('Note saved');
      setSelectedMessage({ ...selectedMessage, internal_notes: internalNote });
      fetchMessages();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(error.message || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleMarkResolved = async (messageId) => {
    await handleStatusChange(messageId, 'resolved');
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      msg.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.message?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const stats = {
    open: messages.filter(m => m.status === 'open').length,
    in_progress: messages.filter(m => m.status === 'in_progress').length,
    resolved: messages.filter(m => m.status === 'resolved').length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
        <div className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Messages & Support</h1>
        <p className="text-gray-400">Manage contact form messages and support requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <WarningCircle size={20} className="text-yellow-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Open</p>
              <p className="text-2xl font-bold text-white">{stats.open}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">In Progress</p>
              <p className="text-2xl font-bold text-white">{stats.in_progress}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Resolved</p>
              <p className="text-2xl font-bold text-white">{stats.resolved}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              fetchMessages();
            }}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Types</option>
            <option value="enquiries">Enquiries</option>
            <option value="support">Support</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              fetchMessages();
            }}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              fetchMessages();
            }}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Messages List */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        {filteredMessages.length === 0 ? (
          <div className="p-12 text-center">
            <Envelope size={48} className="text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No messages found</h3>
            <p className="text-gray-400">
              {searchQuery ? 'Try adjusting your search or filters' : 'Messages will appear here when received'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredMessages.map((message) => (
                <tr key={message.id} className="hover:bg-white/5 cursor-pointer" onClick={() => {
                  setSelectedMessage(message);
                  setInternalNote(message.internal_notes || '');
                }}>
                  <td className="px-6 py-4 text-sm text-white whitespace-nowrap">
                    {new Date(message.submitted_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="text-white font-medium">{message.name}</p>
                      <p className="text-gray-400 text-xs">{message.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {message.subject || 'No subject'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      message.submission_type === 'support'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {message.submission_type === 'support' ? 'Support' : 'Enquiry'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      message.status === 'resolved'
                        ? 'bg-green-500/20 text-green-400'
                        : message.status === 'in_progress'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {message.status === 'in_progress' ? 'In Progress' : message.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkResolved(message.id);
                      }}
                      disabled={message.status === 'resolved'}
                      className="text-sm text-green-400 hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Mark Resolved
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#1A1B1E] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-[#1A1B1E] border-b border-white/10 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">ChatText Details</h2>
                  <p className="text-sm text-gray-400">
                    From {selectedMessage.name} on {new Date(selectedMessage.submitted_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Sender Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Name</label>
                    <p className="text-white">{selectedMessage.name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Email</label>
                    <a href={`mailto:${selectedMessage.email}`} className="text-[#6366F1] hover:underline">
                      {selectedMessage.email}
                    </a>
                  </div>
                  {selectedMessage.company && (
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Company</label>
                      <p className="text-white">{selectedMessage.company}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Type</label>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      selectedMessage.submission_type === 'support'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {selectedMessage.submission_type === 'support' ? 'Support' : 'Enquiry'}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="text-sm font-medium text-gray-300 block mb-2">Status</label>
                  <select
                    value={selectedMessage.status || 'open'}
                    onChange={(e) => handleStatusChange(selectedMessage.id, e.target.value)}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>

                {/* ChatText */}
                <div>
                  <label className="text-sm font-medium text-gray-300 block mb-2">ChatText</label>
                  <div className="p-4 bg-white/5 border border-white/10 rounded-lg text-white whitespace-pre-wrap">
                    {selectedMessage.message}
                  </div>
                </div>

                {/* Internal Notes */}
                <div>
                  <label className="text-sm font-medium text-gray-300 block mb-2">
                    Internal Notes (Admin Only)
                  </label>
                  <textarea
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Add internal notes about this message..."
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50 resize-none"
                  />
                  <button
                    onClick={handleSaveNote}
                    disabled={savingNote}
                    className="mt-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {savingNote ? (
                      <>
                        <CircleNotch size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FloppyDisk size={16} />
                        Save Note
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessagesManagement;
